import { Component, effect, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import {
    OpportunityUserView,
    SearchAvailableOpportunityUsersParameters,
    SearchUsersForOpportunityParameters,
    UserView
} from '@api-proxies';
import { IKebabAction } from '@app/components/shared/grids/custom-cell-actions/custom-cell-actions.component';
import { GridService } from '@app/services/grid.service';
import { OpportunityUserService } from '@http/clients/opportunity-user.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { OpportunityAction } from '@models/enums/OpportunityAction';
import { AwaiterService } from '@services/awaiter.service';
import { CustomMenuItemComponent } from '@shared/custom-menu-item/custom-menu-item.component';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditOpportunityTeamMemberComponent } from '@shared/modals/modal-edit-opportunity-team-member/modal-edit-opportunity-team-member.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { GenericTypeAheadDropdownComponent, ModalService, SnackbarNotificationService } from 'ngx-lib';

import { OPPORTUNITY_USERS_GRID_COLUMNS } from './opportunities-single-team.config';

@Component({
    selector: 'app-opportunities-single-team',
    templateUrl: './opportunities-single-team.component.html',
    styleUrls: ['./opportunities-single-team.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, GenericTypeAheadDropdownComponent, CustomMenuItemComponent]
})
export class OpportunitiesSingleTeamComponent {
    usersDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    isActionAllowed = signal<boolean>(false);
    gridColumns: ColDef[] = [...OPPORTUNITY_USERS_GRID_COLUMNS];

    usersSearchResult?: UserView[];
    usersSearchText: string;
    selectedUser?: UserView;

    opportunityId?: number;
    listEntitiesUpdated = output<void>();

    private readonly opportunityUserService = inject(OpportunityUserService);
    private readonly opportunityService = inject(OpportunityService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);
    private availableActions: number[] = [];

    constructor() {
        this.usersSearchText = '';
        this.opportunityId = this.opportunityService.opportunity()?.id;

        effect(() => {
            const opportunity = this.opportunityService.opportunity();
            if (opportunity?.id) {
                this.opportunityId = opportunity.id;
                this.availableActions = this.opportunityService.availableActions();
                this.isActionAllowed.set(this.availableActions.includes(OpportunityAction.EditUser));
            }
            if (this.opportunityId && !this.usersDatasource) {
                this.initializeComponent();
            }
        });
    }

    getRowIdFn = (params: any) => params.data.id.toString();

    async initializeComponent(): Promise<void> {
        if (!this.opportunityId) return;

        this.setupGridColumns();

        this.createDatasource();
    }

    async onUsersTextChanged(): Promise<void> {
        if (this.opportunityId) {
            this.usersSearchResult = [];

            this.usersSearchResult = await this.opportunityUserService.quickSearch({
                filterText: this.usersSearchText,
                opportunityId: this.opportunityId,
                isActive: true
            } as SearchAvailableOpportunityUsersParameters);
        }
    }

    async onSelectedUser(): Promise<void> {
        if (this.selectedUser?.id && this.opportunityId) {
            await this.awaiter.awaitAction('Saving User', async () => {
                const userSaved = await this.opportunityService.saveUser({
                    userId: this.selectedUser?.id,
                    email: this.selectedUser?.email,
                    userExternalId: this.selectedUser?.externalId,
                    opportunityId: this.opportunityId,
                    opportunityUserUserRoles: []
                } as unknown as OpportunityUserView);

                if (userSaved) {
                    const gridApi = this.gridService.getGridApi();

                    let updated = false;

                    if (gridApi?.applyServerSideTransaction) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            add: [userSaved]
                        });

                        updated = transactionResult?.status === 'Applied';
                    }

                    if (!updated) {
                        this.refreshGridView();
                    }

                    this.noResults.set(false);
                    this.updateOrAddUserInService(userSaved);
                    this.listEntitiesUpdated.emit();
                }
            });
        }
    }

    async editUser(opportunityUser: OpportunityUserView): Promise<void> {
        if (!this.isActionAllowed()) return;

        const userUpdated = await this.modalService.open(ModalEditOpportunityTeamMemberComponent, {
            user: opportunityUser
        });

        if (userUpdated) {
            const gridApi = this.gridService.getGridApi();
            let updated = false;

            if (gridApi) {
                const node = gridApi.getRowNode(opportunityUser.id.toString());

                if (node?.data && gridApi.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        update: [userUpdated]
                    });
                    updated = transactionResult?.status === 'Applied';
                }
            }

            if (!updated) {
                this.refreshGridView();
            }

            this.updateOrAddUserInService(userUpdated);
        }
    }

    async removeUser(opportunityUser: OpportunityUserView): Promise<void> {
        if (!this.isActionAllowed()) return;

        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this team member?',
            title: 'Remove team member'
        });

        if (!responseOk) return;

        const { id: opportunityUserId } = opportunityUser;
        if (!opportunityUserId) return;

        await this.awaiter.awaitAction(
            'Removing user',
            async () => {
                await this.opportunityUserService.delete(opportunityUserId);

                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi) {
                    const node = gridApi.getRowNode(opportunityUserId.toString());

                    if (node?.data && gridApi.applyServerSideTransaction) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }

                    if (removed) {
                        this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                    }
                }

                if (!removed) {
                    this.refreshGridView();
                }

                this.removeUserFromService(opportunityUserId);
                this.listEntitiesUpdated.emit();
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView(): Promise<void> {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit',
                action: (user: OpportunityUserView) => this.editUser(user),
                show: () => this.isActionAllowed()
            },
            {
                label: 'Remove',
                action: (user: OpportunityUserView) => this.removeUser(user),
                show: () => this.isActionAllowed()
            }
        ];

        const baseColumns = this.isActionAllowed()
            ? OPPORTUNITY_USERS_GRID_COLUMNS
            : OPPORTUNITY_USERS_GRID_COLUMNS.filter((col) => col.colId !== 'actions');

        this.gridColumns = baseColumns.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchUsersForOpportunityParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;
        const opportunityId = this.opportunityId ?? 0;

        const searchParams = {
            pageNumber,
            pageSize,
            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            opportunityId
        } as SearchUsersForOpportunityParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.usersDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.opportunityUserService.search(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching users:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }

    private removeUserFromService(userId: number): void {
        const currentUsers = [...(this.opportunityService.opportunityUsers() || [])];
        const index = currentUsers.findIndex((u) => u.id === userId);

        if (index !== -1) {
            currentUsers.splice(index, 1);
            this.opportunityService.updateOpportunityUsers(currentUsers);
        }
    }

    private updateOrAddUserInService(user: OpportunityUserView): void {
        const currentUsers = [...(this.opportunityService.opportunityUsers() || [])];
        const index = currentUsers.findIndex((u) => u.id === user.id);

        if (index !== -1) {
            currentUsers[index] = user;
        } else {
            currentUsers.push(user);
        }

        this.opportunityService.updateOpportunityUsers(currentUsers);
    }
}
